import React from 'react';
import { Input } from './ui/input';

/**
 * Numeric input with safe "empty while editing" UX.
 *
 * - Uses type="text" + inputMode="decimal|numeric" → numeric keyboard on
 *   mobile, no browser spinner, no aggressive coercion that fights the user.
 * - Allows the user to clear the field temporarily; converts to "0" on blur.
 * - Auto-selects current value on focus so the user can quickly replace it.
 * - Stores raw string in state; calculations & submit should use
 *   parseFloat(value) || 0 / Number(value) || 0 at the boundary.
 *
 * Props:
 *   value: string | number (string preferred while editing)
 *   onChange(rawString): callback receiving the raw string value
 *   onBlur(event): optional
 *   decimal: boolean (default true) — allow decimal point
 *   testid: optional data-testid
 *   className: extra classes for the underlying Input
 */
export const NumberInput = React.forwardRef(
  ({ value, onChange, onBlur, decimal = true, className = '', testid, ...props }, ref) => {
    const pattern = decimal ? /^\d*\.?\d*$/ : /^\d*$/;

    const handleChange = (e) => {
      let v = e.target.value;
      if (decimal) v = v.replace(',', '.'); // Indonesian comma → dot
      if (v === '' || pattern.test(v)) {
        onChange(v);
      }
      // else: silently reject (keeps caret stable)
    };

    const handleBlur = (e) => {
      const v = e.target.value;
      if (v === '' || v === '.') {
        onChange('0');
      }
      onBlur && onBlur(e);
    };

    const handleFocus = (e) => {
      try { e.target.select(); } catch (_) { /* noop */ }
    };

    const display = value === undefined || value === null ? '' : String(value);

    return (
      <Input
        ref={ref}
        data-testid={testid}
        type="text"
        inputMode={decimal ? 'decimal' : 'numeric'}
        autoComplete="off"
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        className={className}
        {...props}
      />
    );
  }
);
NumberInput.displayName = 'NumberInput';
